<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border" role="status"><span class="sr-only">Loading...</span></div>
    </div>

    <div v-else-if="order">
      <div class="text-center">
        <h6 class="font-weight-bold mb-2">Order No: &nbsp;{{ order.order }}</h6>
        <p class="py-1 mb-0">Channel:&nbsp;<span class="font-weight-bold text-capitalize">{{ order.source_label }}</span></p>
        <p class="py-1">
          Payment:&nbsp;<span class="badge text-capitalize" :class="paymentBadgeClass" style="font-size:0.75rem">{{ (order.payment_status || 'pending').replace('_',' ') }}</span>
          &nbsp;&nbsp;
          Fulfillment:&nbsp;<span class="badge" :class="fulfillmentBadgeClass" style="font-size:0.75rem">{{ fulfillmentLabel }}</span>
        </p>
      </div>
      <hr />

      <div v-if="trackingEnabled" class="form-row align-items-end mb-3">
        <div class="col-8">
          <label class="small mb-0">Courier tracking number</label>
          <input type="text" class="form-control form-control-sm" v-model="trackingNumber" placeholder="e.g. tracking / waybill number">
        </div>
        <div class="col-4">
          <button class="btn btn-outline-primary btn-sm btn-block" :disabled="savingTracking" @click="saveTracking">
            <span v-if="savingTracking" class="spinner-border spinner-border-sm mr-1"></span>
            Save
          </button>
        </div>
      </div>

      <p v-if="isTerminalStatus" class="text-center text-muted py-2 mb-0">
        This order is <strong>{{ fulfillmentLabel }}</strong> — no further packing actions are available.
      </p>

      <template v-else>
        <h6>Scan items</h6>
        <small class="text-muted d-block mb-2">
          Use a hardware scanner, the camera button, or check items off manually. Each item ships — and its stock is
          deducted — the moment its full quantity is scanned, so an order can go out partially if not everything's ready yet.
        </small>

        <div class="input-group mb-2">
          <input
            ref="scanInput"
            type="text"
            class="form-control"
            placeholder="Scan or type SKU, then press Enter"
            v-model="scanBuffer"
            @keyup.enter="handleScanSubmit"
            autofocus
          >
          <div class="input-group-append">
            <button class="btn btn-outline-secondary" type="button" @click="handleScanSubmit">
              <i class="bi bi-check"></i> Scan
            </button>
            <button v-if="cameraSupported" class="btn btn-outline-secondary" type="button" @click="toggleCamera">
              <i class="bi bi-camera"></i> {{ cameraOpen ? 'Stop Camera' : 'Camera Scan' }}
            </button>
          </div>
        </div>

        <video v-if="cameraOpen" ref="video" class="w-100 mb-2" style="max-height:220px;background:#000" autoplay muted playsinline></video>

        <table class="table table-sm table-hover">
          <thead>
            <tr><th>Item</th><th>SKU</th><th class="text-center">Scanned</th><th class="text-right">&nbsp;</th></tr>
          </thead>
          <tbody>
            <tr v-for="item in order.items" :key="item.id">
              <td>{{ item.product || 'Item' }}</td>
              <td><small>{{ item.sku || '—' }}</small></td>
              <td class="text-center">
                <i v-if="item.deducted_from_inventory" class="bi bi-check-circle-fill text-primary mr-1" title="Shipped"></i>
                {{ item.scanned_qty }} / {{ item.qty }}
                <small v-if="item.deducted_from_inventory" class="text-muted d-block">Shipped</small>
              </td>
              <td class="text-right">
                <button class="btn btn-sm btn-outline-secondary mr-1" :disabled="item.scanned_qty <= 0 || item.deducted_from_inventory" @click="scan(item, -1)">-1</button>
                <button class="btn btn-sm btn-outline-primary" :disabled="item.scanned_qty >= item.qty || item.deducted_from_inventory" @click="scan(item, 1)">+1</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="custom-control custom-checkbox my-2">
          <input type="checkbox" class="custom-control-input" id="manualConfirm" v-model="manualConfirm">
          <label class="custom-control-label" for="manualConfirm">I've manually verified all items without scanning</label>
        </div>

        <button class="btn btn-block btn-primary" :disabled="!canDispatch || dispatching" @click="dispatch">
          <span v-if="dispatching" class="spinner-border spinner-border-sm mr-1"></span>
          Mark Dispatched
        </button>
        <small v-if="!canDispatch" class="text-danger d-block text-center mt-1">
          Scan every item (or check manual verification) before dispatching.
        </small>
      </template>

      <template v-if="['dispatched', 'delivered', 'partially_returned'].includes(order.fulfillment_status)">
        <hr />
        <h6>Returns</h6>
        <div class="form-row align-items-end mb-2">
          <div class="col-6">
            <label class="small mb-0">Item</label>
            <select class="form-control form-control-sm" v-model="returnItemId">
              <option value="">Select item</option>
              <option v-for="item in order.items" :key="item.id" :value="item.id">{{ item.product }} ({{ item.sku }})</option>
            </select>
          </div>
          <div class="col-3">
            <label class="small mb-0">Qty</label>
            <input type="number" min="0.01" step="1" class="form-control form-control-sm" v-model.number="returnQty">
          </div>
          <div class="col-3">
            <button class="btn btn-outline-danger btn-sm" :disabled="!returnItemId || !returnQty" @click="recordReturn">Record Return</button>
          </div>
        </div>
        <table class="table table-sm" v-if="returns.length">
          <thead><tr><th>Date</th><th>Qty</th><th>Amount</th><th>Type</th></tr></thead>
          <tbody>
            <tr v-for="r in returns" :key="r.id">
              <td>{{ new Date(r.return_date).toLocaleDateString() }}</td>
              <td>{{ r.qty_returned }}</td>
              <td>{{ r.amount_returned }}</td>
              <td class="text-capitalize">{{ r.type }}</td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>

    <p v-else class="text-center text-muted py-4">Order not found.</p>
  </div>
</template>

<script>
export default {
  name: 'PackDispatch',
  props: {
    orderId: { type: String, required: true },
    trackingEnabled: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      order: null,
      returns: [],
      scanBuffer: '',
      manualConfirm: false,
      dispatching: false,
      trackingNumber: '',
      savingTracking: false,
      cameraOpen: false,
      cameraSupported: 'BarcodeDetector' in window,
      barcodeDetector: null,
      returnItemId: '',
      returnQty: 1,
    }
  },
  computed: {
    fulfillmentLabel() {
      const map = { not_released: 'Not Released', packing: 'Packing', partially_dispatched: 'Partially Dispatched', dispatched: 'Dispatched', delivered: 'Delivered', returned: 'Returned', partially_returned: 'Partially Returned', cancelled: 'Cancelled' }
      return map[this.order?.fulfillment_status] || 'Not Released'
    },
    fulfillmentBadgeClass() {
      const map = { not_released: 'badge-dark', packing: 'badge-secondary', partially_dispatched: 'badge-dark', dispatched: 'badge-primary', delivered: 'badge-primary', returned: 'badge-danger', partially_returned: 'badge-danger', cancelled: 'badge-secondary' }
      return map[this.order?.fulfillment_status] || 'badge-dark'
    },
    paymentBadgeClass() {
      const map = { pending: 'badge-dark', paid: 'badge-primary', partially_paid: 'badge-secondary', refunded: 'badge-danger', partially_refunded: 'badge-danger', voided: 'badge-secondary' }
      return map[this.order?.payment_status] || 'badge-dark'
    },
    isTerminalStatus() {
      return ['dispatched', 'delivered', 'returned', 'cancelled'].includes(this.order?.fulfillment_status)
    },
    canDispatch() {
      if (this.manualConfirm) return true
      if (!this.order) return false
      return this.order.items.every(i => i.scanned_qty >= i.qty)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      axios.get(`/api/integrations/fulfillment/${this.orderId}`)
        .then(res => { this.order = res.data; this.trackingNumber = res.data.courier_tracking_number || '' })
        .catch(() => { this.order = null })
        .finally(() => { this.loading = false; this.loadReturns() })
    },
    loadReturns() {
      axios.get(`/api/integrations/returns/${this.orderId}`).then(res => { this.returns = res.data }).catch(() => {})
    },
    handleScanSubmit() {
      const code = (this.scanBuffer || '').trim()
      this.scanBuffer = ''
      if (!code || !this.order) return
      const item = this.order.items.find(i => i.sku && i.sku.toLowerCase() === code.toLowerCase())
      if (!item) {
        this.$toasts.push({ type: 'error', message: `No item on this order matches "${code}".` })
        return
      }
      if (item.scanned_qty >= item.qty) {
        this.$toasts.push({ type: 'error', message: `${item.product} is already fully scanned.` })
        return
      }
      this.scan(item, 1)
    },
    scan(item, delta) {
      axios.post(`/api/integrations/fulfillment/${this.orderId}/scan`, { order_item_id: item.id, delta })
        .then(res => {
          item.scanned_qty = res.data.scanned_qty
          if (this.order.fulfillment_status === 'not_released') this.order.fulfillment_status = 'packing'
        })
        .catch(err => {
          this.$toasts.push({ type: 'error', message: this.errorMessage(err, 'Could not update scan.') })
        })
    },
    dispatch() {
      this.dispatching = true
      axios.post(`/api/integrations/fulfillment/${this.orderId}/dispatch`, { manual_confirm: this.manualConfirm })
        .then(res => {
          this.order.fulfillment_status = res.data.fulfillment_status
          this.$toasts.push({ type: 'success', message: 'Order dispatched — inventory deducted.' })
          this.$emit('updated', { orderId: this.orderId, fulfillment_status: res.data.fulfillment_status })
        })
        .catch(err => {
          this.$toasts.push({ type: 'error', message: this.errorMessage(err, 'Could not dispatch order.') })
        })
        .finally(() => { this.dispatching = false })
    },
    saveTracking() {
      this.savingTracking = true
      axios.patch(`/api/integrations/fulfillment/${this.orderId}/tracking`, { tracking_number: this.trackingNumber })
        .then(res => {
          this.order.courier_tracking_number = res.data.courier_tracking_number
          this.$toasts.push({ type: 'success', message: 'Tracking number saved.' })
        })
        .catch(err => {
          this.$toasts.push({ type: 'error', message: this.errorMessage(err, 'Could not save tracking number.') })
        })
        .finally(() => { this.savingTracking = false })
    },
    recordReturn() {
      axios.post(`/api/integrations/returns/${this.orderId}`, { order_item_id: this.returnItemId, qty: this.returnQty })
        .then(res => {
          this.order.fulfillment_status = res.data.fulfillment_status
          this.returnItemId = ''
          this.returnQty = 1
          this.$toasts.push({ type: 'success', message: 'Return recorded — inventory restored.' })
          this.loadReturns()
          this.$emit('updated', { orderId: this.orderId, fulfillment_status: res.data.fulfillment_status })
        })
        .catch(err => {
          this.$toasts.push({ type: 'error', message: this.errorMessage(err, 'Could not record return.') })
        })
    },
    errorMessage(err, fallback) {
      // Laravel's default ValidationException JSON puts the actual reason
      // under `errors.<field>[0]`, not the generic top-level `message`
      // ("The given data was invalid.") - surface the specific one.
      const errors = err.response?.data?.errors
      const specific = errors && Object.values(errors)[0]?.[0]
      return specific || err.response?.data?.message || fallback
    },
    async toggleCamera() {
      if (this.cameraOpen) {
        this.stopCamera()
        return
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        this.cameraOpen = true
        this.$nextTick(() => {
          this.$refs.video.srcObject = stream
          this.barcodeDetector = new window.BarcodeDetector()
          this.scanLoop()
        })
      } catch (e) {
        this.$toasts.push({ type: 'error', message: 'Camera unavailable — use the scan box or manual confirmation instead.' })
      }
    },
    stopCamera() {
      this.cameraOpen = false
      const stream = this.$refs.video?.srcObject
      if (stream) stream.getTracks().forEach(t => t.stop())
    },
    async scanLoop() {
      if (!this.cameraOpen || !this.$refs.video) return
      try {
        const codes = await this.barcodeDetector.detect(this.$refs.video)
        if (codes.length) {
          this.scanBuffer = codes[0].rawValue
          this.handleScanSubmit()
        }
      } catch (e) { /* transient decode errors are expected between frames */ }
      requestAnimationFrame(() => this.scanLoop())
    },
  },
  beforeDestroy() {
    this.stopCamera()
  },
}
</script>
